import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type {
  PermissionState,
  RenderOptions,
  ResourcePermissionGateProps,
} from '@/components/common/PermissionGate/types'
import {
  getDefaultTooltipMessage,
  renderWithPermission,
  validateAsChildProps,
} from '@/components/common/PermissionGate/utils'

import { usePermissionCheck } from '@/hooks/permissions/usePermissionCheck'

import type { Permission } from '@/types/Permission'

/**
 * Higher-Order Component for single resource permission checks.
 *
 * @example
 * // Hide delete button for users without write permission
 * <ResourcePermissionGate
 *   resourceType="pursuit"
 *   resourceId={pursuitId}
 *   permission="write"
 *   fallback="hide"
 * >
 *   <Button>Delete</Button>
 * </ResourcePermissionGate>
 *
 * @example
 * // Disable with custom tooltip
 * <ResourcePermissionGate
 *   resourceType="file"
 *   resourceId={fileId}
 *   permission="write"
 *   fallback="disable"
 *   tooltip="You cannot edit this file"
 *   asChild
 * >
 *   <DropdownMenuItem>Edit File</DropdownMenuItem>
 * </ResourcePermissionGate>
 *
 * @example
 * // Require multiple permissions (user needs BOTH read AND write)
 * <ResourcePermissionGate
 *   resourceType="pursuit"
 *   resourceId={pursuitId}
 *   permission={['read', 'write']}
 *   requireAll={true}
 *   fallback="disable"
 * >
 *   <Button>Advanced Edit</Button>
 * </ResourcePermissionGate>
 */
export const ResourcePermissionGate = forwardRef<
  HTMLElement,
  ResourcePermissionGateProps
>(
  (
    {
      resourceType,
      resourceId,
      permission,
      requireAll = false,
      fallback = 'hide',
      fallbackComponent,
      loadingComponent,
      tooltip,
      tooltipProps,
      tooltipContentProps,
      children,
      asChild = false,
      onPermissionChange,
    },
    ref,
  ) => {
    // Validate props
    if (asChild) {
      validateAsChildProps(children, asChild)
    }

    // Normalize permission to array
    const permissions = useMemo(
      () => (Array.isArray(permission) ? permission : [permission]),
      [permission],
    )

    // Create select function to derive hasPermission directly from the hook
    const select = useCallback(
      (data: Map<Permission, boolean>) => {
        const results = permissions.map((p) => data.get(p) === true)
        return requireAll
          ? results.every(Boolean) // ALL permissions required
          : results.some(Boolean) // ANY permission sufficient
      },
      [permissions, requireAll],
    )

    // Check resource permissions with select to derive hasPermission directly
    const {
      data: hasPermission = false,
      isLoading,
      isError,
    } = usePermissionCheck({
      resourceType,
      resourceId,
      permissions,
      enabled: !!resourceId,
      select,
    })

    const permissionStateKey = useMemo(
      () =>
        JSON.stringify({
          resourceType,
          resourceId,
          permissions,
          requireAll,
        }),
      [permissions, requireAll, resourceId, resourceType],
    )

    // Track previous hasPermission value for change detection
    const prevHasPermission = useRef<boolean | null>(null)
    const lastSettledPermission = useRef<{
      key: string
      hasPermission: boolean
    } | null>(null)

    const lastSettledHasPermission =
      lastSettledPermission.current?.key === permissionStateKey
        ? lastSettledPermission.current.hasPermission
        : null
    const useLastSettledPermission =
      isLoading && lastSettledHasPermission !== null

    // Call onPermissionChange only when hasPermission actually changes
    useEffect(() => {
      if (isLoading || isError) return

      lastSettledPermission.current = {
        key: permissionStateKey,
        hasPermission,
      }

      if (onPermissionChange && prevHasPermission.current !== hasPermission) {
        prevHasPermission.current = hasPermission
        onPermissionChange(hasPermission)
      }
    }, [
      hasPermission,
      isError,
      isLoading,
      onPermissionChange,
      permissionStateKey,
    ])

    // Generate smart tooltip if not provided
    const finalTooltip =
      tooltip === undefined
        ? getDefaultTooltipMessage(resourceType, permission, 'single')
        : tooltip

    const permissionState: PermissionState = {
      hasPermission: useLastSettledPermission
        ? lastSettledHasPermission
        : hasPermission,
      isLoading: useLastSettledPermission ? false : isLoading,
      isError: useLastSettledPermission ? false : isError,
    }

    const renderOptions: RenderOptions = {
      fallback,
      fallbackComponent,
      loadingComponent,
      tooltip: finalTooltip,
      tooltipProps,
      tooltipContentProps,
    }

    const result = renderWithPermission(
      permissionState,
      children,
      renderOptions,
    )

    // Handle forwardRef when asChild is true
    if (asChild && React.isValidElement(result) && ref) {
      return React.cloneElement(result, {
        ref,
        ...(result.props || {}),
      } as any)
    }

    return result
  },
)

ResourcePermissionGate.displayName = 'ResourcePermissionGate'
